import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reject, map, delay, first, filter, sortBy } from 'lodash';
import Fa from 'react-fontawesome';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Link, withRouter } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import { OverlayTrigger, Tooltip, Dropdown, MenuItem, NavItem } from 'react-bootstrap';

import ProgressBar from '../../../../components/progress-bar';
import DatetimeDisplay from '../../../../components/date-time-display';
import RunAutomationButton from '../../components/RunAutomationButton';
import TruVoiceDomainAuthorization from '../../../../components/tv-domain-authorizations';
import AllowMutipleGroupOnSync from './fields/AllowMutipleGroupOnSync';
import TestEmailButton from '../../components/TestSendEmailForm';
import APIKeyInfo from '../../components/APIKeyInfo';

import {
  fetchAllFeedbackRequestAutomations,
  deleteFeedbackRequestAutomation,
  cloneFeedbackRequestAutomation,
  switchSystemFeedbackRequestAutomation,
  orderFeedbackRequestAutomations,
} from '../../SetupActions';

import styles from './styles.css';

/** A little function to help us with reordering the result */
const mutatedReorder = (list, startIndex, endIndex) => {
  const [removed] = list.splice(startIndex, 1);
  list.splice(endIndex, 0, removed);
  return list;
};

class FeedbackRequestAutomationIndex extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      loaded: false,
      settings: [],
      deleting: {}, // indexed by id
      duplicating: {}, // indexed by id
      updating: {}, // indexed by id
      confirmingId: null,
      openId: null,
      dragging: false,
      blinkId: null,
    };
    this.deleteAutomation = this.deleteAutomation.bind(this);
    this.duplicateAutomation = this.duplicateAutomation.bind(this);
    this.switchDefaultAutomation = this.switchDefaultAutomation.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
  }
  
  componentDidMount() {
    this.fetchData();
  }


  cleanupList(settings) {
    // is_deleted
    // sort_order
    return sortBy(settings, [(s) => { return s.is_deleted ? 1 : 0 }, 'sort_order']);
  }

  /** Get and push automations from server to state */
  fetchData() {
    this.setState({ loading: true, loaded: false }, () => {
      this.props.dispatch(fetchAllFeedbackRequestAutomations(true))
        .then(
          ({ settings }) => {
            this.setState({ settings: this.cleanupList(settings), loaded: true, loading: false });
          },
          () => {
            // TODO:
          },
        );
    });
  }

  /** Dispatch automation reorder and update state automations */
  onDragEnd(result) {
    const { source, destination } = result;
    if (!this.state.dragging && source.index !== destination.index) {
      const destinationList = [...this.state.settings];
      
      const draggedId = this.state.settings[source.index] ? this.state.settings[source.index]._id : null;

      mutatedReorder(
        destinationList,
        source.index,
        destination.index,
      );
      
      const positions = map(destinationList, '_id');
      this.setState({ dragging: true, draggedId: null }, () => {
        this.props.dispatch(orderFeedbackRequestAutomations({ ids: positions }, true))
          .then(
            ({ settings }) => {
              this.setState({ settings: this.cleanupList(settings), dragging: false, draggedId });
            },
            () => {
              this.setState({ dragging: false, draggedId });
            },
          );
      });
    }
  }

  /** Dispatch delete automation action and update state automations */
  deleteAutomation(setting) {
    const { deleting, confirmingId } = this.state;

    if (confirmingId !== setting._id) {
      this.setState({ confirmingId: setting._id });
      return;
    }
    if (deleting[setting._id] === true) {
      return;
    }

    this.setState({ deleting: { ...deleting, [setting._id]: true } }, () => {
      this.props.dispatch(deleteFeedbackRequestAutomation(setting._id))
        .then(
          () => {
            const newSettings = reject(this.state.settings, (s) => s._id === setting._id);
            newSettings.push({
              ...setting,
              is_deleted: true,
            });
            this.setState({ confirmingId: null, settings: newSettings, deleting: { ...deleting, [setting._id]: false } });
          },
          () => {
            this.setState({ confirmingId: null, deleting: { ...deleting, [setting._id]: false } });
          },
        );
    });
  }

  /** Set target automation on action dropdown toggle */
  handleToggle(open, e, s, openId) {
    if (open) {
      this.setState({ openId });
    } else if (s.source === 'rootClose') {
      this.setState({ openId: null, confirmingId: {} });
    }
  }

  /** Dispatch clone automation action and update state automations */
  duplicateAutomation(setting) {
    const { duplicating } = this.state;

    if (duplicating[setting._id] === true) {
      return;
    }

    this.setState({ duplicating: { ...duplicating, [setting._id]: true } }, () => {
      this.props.dispatch(cloneFeedbackRequestAutomation(setting._id))
        .then(
          (newSetting) => {
            const newSettings = [...this.state.settings, newSetting];
            this.setState({ openId: null, settings: newSettings, duplicating: { ...duplicating, [setting._id]: false } });
          },
          () => {
            this.setState({ openId: null, duplicating: { ...duplicating, [setting._id]: false } });
          },
        );
    });
  }

  /** Dispatch change default automation action */
  switchDefaultAutomation(setting) {
    if (setting.is_system) {
      return;
    }
    if (!this.state.updating[setting._id]) {
      this.setState({ updating: { ...this.state.updating, [setting._id]: true } }, () => {
        this.props.dispatch(switchSystemFeedbackRequestAutomation(setting._id))
          .then(
            (updatedSettings) => {
              const newSettings = this.state.settings?.map((oldSetting) => {
                return updatedSettings.find(updatedSetting => updatedSetting._id === oldSetting._id) || oldSetting;
              });
              this.setState({ openId: null, settings: newSettings, updating: { ...this.state.updating, [setting._id]: false } });
            },
            () => {
              this.setState({ openId: null, updating: { ...this.state.updating, [setting._id]: false } });
            },
          );
      });
      
    }
    
  }

  /** Render dropdown menu of actions against specific automation */
  renderActions(setting) {
    const { deleting, duplicating, updating, confirmingId, openId } = this.state;

    const isDeleting = deleting[setting._id] === true;
    const isDuplicating = duplicating[setting._id] === true;
    const isConfirming = confirmingId === setting._id;
    const isUpdating = updating[setting._id] === true;
    const isSystem = setting.is_system === true;

    let deleteBtn;

    if (isDeleting) {
      deleteBtn = (
        <span className="text-danger"><Fa name="spinner" spin /> Deleting</span>
      );
    } else if (isConfirming) {
      deleteBtn = (
        <span className="text-danger"><Fa name="trash" /> Sure?</span>
      );
    } else {
      deleteBtn = (
        <span><Fa name="trash" /> Delete</span>
      );
    }

    let duplicateBtn;
    if (isDuplicating) {
      duplicateBtn = (
        <span><Fa name="spinner" spin /> Duplicating</span>
      );
    } else {
      duplicateBtn = (
        <span><Fa name="copy" /> Duplicate</span>
      );
    }

    const isDisabled = isDeleting || isDuplicating || isUpdating;
    return (
      <Dropdown
        bsSize="sm"
        id={`settings-${setting._id}`}
        onToggle={(o, e, s) => {
          this.handleToggle(o, e, s, setting._id);
        }}
        open={(openId === setting._id)}
      >
        <Dropdown.Toggle>Actions</Dropdown.Toggle>
        <Dropdown.Menu className="pull-right">
          <MenuItem disabled={isDisabled} eventKey={setting} onSelect={this.duplicateAutomation}>
            {duplicateBtn}
          </MenuItem>
          {!isSystem &&
            <MenuItem disabled={isDisabled} eventKey={setting} onSelect={this.switchDefaultAutomation}>
              <span><Fa name="star" /> Set as Default</span>
            </MenuItem>
          }
          {!isSystem &&
            <MenuItem disabled={isDisabled} eventKey={setting} onSelect={this.deleteAutomation}>
              {deleteBtn}
            </MenuItem>
          }
        </Dropdown.Menu>
      </Dropdown>
    )
  }

  renderStar(setting) {
    const { deleting, duplicating, updating } = this.state;
    const isDeleting = deleting[setting._id] === true;
    const isDuplicating = duplicating[setting._id] === true;
    const isUpdating = updating[setting._id] === true;
    const isDeleted = setting.is_deleted === true;

    let star = null;
    if (setting.is_system) {
      star = (<Fa name="star" className="text-warning" />);
    } else if (isDeleting || isDuplicating) {
      star = (<Fa name="star" className="text-muted" />);
    } else if (isUpdating) {
      star = (<Fa name="spinner" spin className="text-muted" />);
    } else if (isDeleted) {
      star = (<Fa name="star" className="text-muted" />);
    } else {
      star = (
        <a href="" onClick={(e) => { e.preventDefault(); this.switchDefaultAutomation(setting); }}>
          <Fa name="star" className="text-muted" />
        </a>
      );
    }
    return star;
  }

  /** Render row representing automation including name, source, enable status, last modified, and action menu */
  renderRow(setting, index, total) {
    const { dragging, draggedId } = this.state;
    const { location: { pathname } } = this.props;
    const isDeleted = setting.is_deleted === true;

    const tip = (
      <Tooltip id={`${setting._id}-system-automation`}>
        {setting.is_system && <div>System Default Automation</div>}
        {!setting.is_system && <div>Mark as System Default Automation</div>}
      </Tooltip>
    );

    
    const blinking = draggedId === setting._id;

    let arrowName;
    if (dragging) {
      arrowName = 'spinner';
    } else if (total > 1 && index === 0) {
      arrowName = 'long-arrow-down';
    } else if (total > 1 && index === total - 1) {
      arrowName = 'long-arrow-up';
    } else {
      arrowName = 'arrows-v';
    }

    const render = (provided, snapshot) => {
      const atrrs = (provided && snapshot) ? {
        ref: provided.innerRef,
        ...provided.draggableProps,
        className: `${snapshot.isDragging ? styles.dragging : null}`,
        style: provided.draggableProps.style,
      } : {};

      return (
        <tr key={setting._id} {...atrrs}>
          <td
            width="40"
            className="text-center"
            {...(provided ? provided.dragHandleProps : {})}
          >
            <Fa
              name={arrowName}
              spin={dragging}
              className={isDeleted ? 'invisible' : ''}
            />
          </td>
          <td className="text-center">
            {!isDeleted &&
              <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={tip}>
                {this.renderStar(setting)}
              </OverlayTrigger>
            }
            {isDeleted && this.renderStar(setting)}
          </td>
          <td>
            <Link to={`${pathname}/${setting.source === 'account' ? 'accounts' : 'opportunities'}/${setting._id}`}>
              <span className={blinking ? 'animated flash' : ''}>
                <span className={isDeleted ? 'text-muted' : ''}>{setting.name}</span>
              </span>
            </Link>
          </td>
          <td>
            <span className="label label-default text-uppercase">{setting.source}</span>
          </td>
          <td>
            {!isDeleted && <span className={`label ${setting.enabled ? 'label-success' : 'label-danger'}`}>{setting.enabled ? 'YES' : 'NO'}</span>}
            {isDeleted && <span className="label label-danger">DELETED</span>}
          </td>
          <td>
            <DatetimeDisplay value={setting.updated_at} format="LLL" />
          </td>
          <td>
            {!isDeleted && this.renderActions(setting)}
          </td>
        </tr>
      );
    };

    if (isDeleted) {
      return render();
    }

    return (
      <Draggable draggableId={setting._id} key={setting._id} index={index}>
        {render}
      </Draggable>
    );
  }

  /** Render create button with dropdown to specify opportunity/deal or account/company automation */
  renderCreateNewButton() {
    const { location: { pathname } } = this.props;
    return (
      <Dropdown id="create-new-group">
        <Dropdown.Toggle bsStyle="primary"><Fa name="plus" /> create a new automation group</Dropdown.Toggle>
        <Dropdown.Menu className="pull-right">
          <LinkContainer to={`${pathname}/opportunities/new`}>
            <NavItem>Opportunity/Deal Automation</NavItem>
          </LinkContainer>
          <LinkContainer to={`${pathname}/accounts/new`}>
            <NavItem>Account/Company Automation</NavItem>
          </LinkContainer>
        </Dropdown.Menu>
      </Dropdown>
    )
  }

  /** Render reorderable table with rows representing automations */
  renderTable() {
    const { settings } = this.state;
    const { emergencySettings } = this.props;
    const currentSettings = filter(settings, { is_deleted: false });
    const deletedSettings = filter(settings, { is_deleted: true });
    const hasDeletedRecords = deletedSettings.length > 0;

    return (
      <div>
        <div className="clearfix">
          <div className="pull-left" style={{ paddingTop: '5px' }}>
            <AllowMutipleGroupOnSync />
          </div>
          <div className="pull-right">
            {emergencySettings && !emergencySettings.disabled && <RunAutomationButton />}
            {' '}
            {this.renderCreateNewButton()}
          </div>
        </div>
        <p />
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Fragment>
            <table className={`table table-bordered table-condensed table-striped ${styles.table}`}>
              <thead>
                <tr>
                  <th width="40" />
                  <th colSpan="2">Current Automations:</th>
                  <th width="100">Source</th>
                  <th width="50">Enabled</th>
                  <th width="200">Last Modified</th>
                  <th width="80">Action</th>
                </tr>
              </thead>
              <Droppable droppableId="drop">
                {(provided, snapshot) => (
                  <tbody
                    ref={provided.innerRef}
                    className={snapshot.isDraggingOver ? styles.DraggingOver : null}
                  >
                    {currentSettings.map((s, index) => this.renderRow(s, index, currentSettings.length))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
              {hasDeletedRecords &&
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <th colSpan="6">Deleted Automations:</th>
                  </tr>
                </thead>
              }
              {hasDeletedRecords &&
                <tbody>
                  {deletedSettings.map((s, index) => this.renderRow(s, index, deletedSettings.length))}
                </tbody>
              }
            </table>
          </Fragment>
        </DragDropContext>


        <p />
        <div className="clearfix">
          <div className="pull-left"><TruVoiceDomainAuthorization /></div>
          <div className="pull-left">&nbsp;&nbsp;<TestEmailButton>Send a Test Email</TestEmailButton></div>
        </div>

        <APIKeyInfo />
        
        <p>&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>
      </div>
    );
  }

  /** Render the feedback request automation index page */
  render() {
    const { loading, loaded, settings } = this.state;
    const isLoading = loading || !loaded;
    const { location: { pathname } } = this.props;
    if (isLoading) {
      return (
        <div>
          <ProgressBar />
        </div>
      );
    }
    if (settings.length === 0) {
      return <div className="alert alert-danger">No automation found. <Link to={`${pathname}/new`}>create a new automation group</Link></div>;
    }
    return this.renderTable();
  }
}

/** Retrieve data from store as props */
function mapStateToProps(/* state */) {
  return {
  };
}

FeedbackRequestAutomationIndex.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  emergencySettings: PropTypes.shape({
    disabled: PropTypes.bool,
  }),
};
FeedbackRequestAutomationIndex.defaultProps = {
  emergencySettings: null,
};
FeedbackRequestAutomationIndex.contextTypes = {
};

export default withRouter(connect(mapStateToProps)(FeedbackRequestAutomationIndex));
